import { z } from 'zod';
import { LocalDate, TimeInterval } from '../primitives.js';
/**
 * The weekly-schedule grammar shared by business fallback hours and per-staff
 * schedules (doc 09 §5.2) — one shape, so business hours and a staff member's
 * personal schedule parse identically.
 *
 * `hours` keys are weekday numbers as strings, 0=Sun … 6=Sat (JS `getDay()`),
 * each mapping to ZERO OR MORE open intervals (more than one = a split shift).
 * `exceptions` override a specific date (an empty array = closed / off that day).
 */
export const WeeklyScheduleSchema = z.object({
    hours: z.record(z.enum(['0', '1', '2', '3', '4', '5', '6']), z.array(TimeInterval)).default({}),
    exceptions: z.record(LocalDate, z.array(TimeInterval)).default({}),
});
/** Two calendar years, at their longest (one includes 29 February). */
export const MAX_BOOKING_HORIZON_DAYS = 731;
/** The most calendar days of notice a business may require — mirrors `firestore.rules`. */
export const MAX_MIN_NOTICE_DAYS = 30;
/**
 * `businesses/{businessId}/availability/weekly` (single doc) — the business's
 * FALLBACK working hours plus booking policy. Used for staff members without a
 * personal schedule, and for all bookings when the business has no staff
 * (doc 09 D2/D3). Extends the weekly-schedule grammar so the same intervals map
 * powers both places; existing availability docs parse unchanged.
 */
export const AvailabilitySchema = WeeklyScheduleSchema.extend({
    /**
     * LEGACY — how far ahead callers could book, in days. Kept so stored and
     * published documents still parse, but no longer the voice assistant's limit:
     * that is two calendar years from today in the business timezone (doc 56 E),
     * computed per call, so a business saved with the old default of 30 books
     * just as far ahead as a new one. Up to 731 (the longest two years can be)
     * is accepted; the default deliberately stays 30, because Functions built
     * before doc 56 validate this field with a maximum of 365 — a portal that
     * started writing 731 would make their config publish fail. Nothing reads
     * the value for booking any more, so the default changes nothing.
     */
    bookingHorizonDays: z.number().int().min(1).max(MAX_BOOKING_HORIZON_DAYS).default(30),
    /** No bookings sooner than this many minutes from now. */
    minNoticeMin: z.number().int().min(0).max(10080).default(60),
    /**
     * No bookings before this many CALENDAR days from today, in the business
     * timezone (doc 84): 0 = today is bookable, 1 = from tomorrow (no same-day
     * bookings), 2 = from the day after tomorrow. Unlike `minNoticeMin` it is a
     * date, not a duration — "from tomorrow" opens tomorrow at midnight, whatever
     * the time a caller rings. Both apply; the later cutoff wins. Absent on every
     * document saved before it existed, which reads as 0: nothing changes.
     */
    minNoticeDays: z.number().int().min(0).max(MAX_MIN_NOTICE_DAYS).default(0),
    /**
     * How much notice the assistant requires to cancel or move an existing
     * appointment (doc 14 D4). 0 = no restriction, which is the behaviour before
     * this field existed. Applies to the appointment being changed; the NEW slot
     * of a reschedule is governed by the booking notice as any booking would be.
     */
    cancellationNoticeMin: z.number().int().min(0).max(10080).default(0),
});
/** Sensible default: Mon–Fri 9:00–17:00, closed weekends. */
export const DEFAULT_AVAILABILITY = {
    hours: {
        '1': [{ startMin: 540, endMin: 1020 }],
        '2': [{ startMin: 540, endMin: 1020 }],
        '3': [{ startMin: 540, endMin: 1020 }],
        '4': [{ startMin: 540, endMin: 1020 }],
        '5': [{ startMin: 540, endMin: 1020 }],
    },
    exceptions: {},
    bookingHorizonDays: 30,
    minNoticeMin: 60,
    minNoticeDays: 0,
    cancellationNoticeMin: 0,
};
//# sourceMappingURL=availability.js.map